import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators, ValidatorFn, ValidationErrors } from "@angular/forms";

import { ValidationService } from "app/common/validation/validation.service";
import { GatewayEditWizardService, PrivateGatewayEdit } from "../gateway-edit-wizard.service";
import { AbstractWizardStepComponent } from "app/administrative/wizard-dialog/abstract-wizard-step.component";
import { GatewayEditNameComponent } from "../gateway-edit-name/gateway-edit-name.component";
import { GatewayEditDnsComponent } from "../gateway-edit-dns/gateway-edit-dns.component";
import * as gatewayIcon from "app/../assets/svg/gatewayDashboardIcon.svg";
import * as firewallIcon from "app/../assets/svg/firewall-svgrepo-com.svg";
import { CrossFieldErrorMatcher } from "../cross-field-error-matcher";
import { GatewaysService } from "../../gateways.service";

@Component({
	selector: "sdp-gateway-edit-ip-ports",
	templateUrl: "./gateway-edit-ip-ports.component.html"
})
export class GatewayEditIpPortsComponent extends AbstractWizardStepComponent<PrivateGatewayEdit> implements OnInit {
	public readonly internetIcon = { type: "fa", name: "globe", cssClass: "" };
	public readonly gatewayIcon = { type: "image", name: "gateway", src: gatewayIcon, cssClass: "sdp-dashboard-img-icon" };
	public readonly firewallIcon = { type: "image", name: "firewall", src: firewallIcon, cssClass: "" };
	public readonly warningIcon = { type: "fa", name: "exclamation-triangle", cssClass: "sdp-generic-icon-error sdp-gayeway-warning-icon" };

	private readonly OVERLAP_ERROR = "hasOverlappingIpPorts";

	public OPENVPNPORT!: number;
	public FWKNOPPORT!: number;
	public form!: FormGroup;
	public publicIpMatcher = new CrossFieldErrorMatcher([this.OVERLAP_ERROR]);
	public publicPortMatcher = new CrossFieldErrorMatcher(["valuesAreSame"]);


	constructor(
		private formBuilder: FormBuilder,
		private validationService: ValidationService,
		private service: GatewayEditWizardService,
		private gatewayService: GatewaysService
	) {
		super();
	}

	public ngOnInit() {
		const formValidators: ValidationErrors[] = [
			// the fields being passed in can not be the same value in the form
			this.validationService.valuesCannotBetheSame("publicOpenVpnPort", "publicFwknopPort")
		];
		if (this.data.editGateway.state === "active" || this.data.activateWhenUpdated) {
			// validates that there are no other active gateways that have same ip and at least one port is the same
			// added to validators only if the state of gateway under edit is state===active or will be actived on update
			formValidators.push(this.service.hasOverlappingIpPortsValidator(this.data.allGateways));
		}

		const publicIpPorts = this.gatewayService.getPublicHostAndPorts(this.data.editGateway.hostAndPorts);
		const privateIpPorts = this.gatewayService.getPrivateHostAndPorts(this.data.editGateway.hostAndPorts);
		this.OPENVPNPORT = privateIpPorts.openvpnPort;
		this.FWKNOPPORT = privateIpPorts.fwknopPort;

		this.form = this.formBuilder.group({
			publicIp: [publicIpPorts.host, Validators.compose([Validators.required, this.validationService.singleIp])],
			publicOpenVpnPort: [
				publicIpPorts.openvpnPort,
				Validators.compose([
					Validators.required,
					this.validationService.port,
				])
			],
			publicFwknopPort: [
				publicIpPorts.fwknopPort,
				Validators.compose([
					Validators.required,
					this.validationService.port,
				])
			],
			privateIp: [privateIpPorts.host, Validators.compose([Validators.required, this.validationService.singleIp])]
		},
			{
				validators: formValidators
			});
	}

	public get title() {
		return "gateway.edit.title";
	}

	public get description() {
		return "gateway.edit.ipPorts.description";
	}

	public get nextStep() {
		return GatewayEditDnsComponent;
	}

	public get previousStep() {
		return GatewayEditNameComponent;
	}

	public get isFormValid() {
		return !this.form || this.form.valid || this.isHasOverlap();
	}

	public performPrevious() {
		this.service.processIpPorts(this.form.getRawValue());
	}

	public performNext() {
		this.service.processIpPorts(this.form.getRawValue());
	}

	public get showProgressBar() {
		return false;
	}

	/**
	 * @returns true if the only error on the form is the ip/port overlap error, false otherwise
	 * More specifically, returns false if there are any form controls with errors, or any form-level
	 * errors other than this.OVERLAP_ERROR
	 */
	private isHasOverlap() {
		if (!!this.form && this.form.hasError(this.OVERLAP_ERROR)) {
			// has the overlap error, check if there are any other errors
			const controlErrors = Object.keys(this.form.controls)
				.map((control) => {
					return this.form.get(control)!.errors;
				})
				.filter(notNull)
				.map((error) => {
					return Object.keys(error);
				});
			const formErrors = Object.keys(this.form.errors!);
			const flattenedErrors = ([] as string[]).concat(...controlErrors).concat(...formErrors);
			return flattenedErrors.length === 1;
		} else {
			return false;
		}
	}
}

function notNull(error: ValidationErrors | null): error is ValidationErrors {
	return !!error;
}
